/**
 * Utilitários reutilizáveis para paginação server-side e exportação (CSV/PDF).
 * Usado por CRUDUsuarios e CRUDPessoas (e outros CRUDs com tabela paginada).
 *
 * state: { pageNumber, numberOfRows, serverSortBy, serverSortDesc, searchFilters, lastItemsPerPageBeforeTodos, total }
 */

function toArray(val) {
  if (val == null) return [];
  return Array.isArray(val) ? val : [val];
}

function normalizeSortDesc(val) {
  if (val == null) return [];
  const arr = Array.isArray(val) ? val : [val];
  return arr.map((d) => d === true || d === 'true');
}

/**
 * Monta o objeto de parâmetros para a requisição de uma página (GET).
 * @param {Object} options - { page, itemsPerPage, sortBy, sortDesc }
 * @param {Object} state - { pageNumber, numberOfRows, serverSortBy, serverSortDesc, searchFilters }
 * @returns {Object} params para axios (page, rowsPerPage, total: 'true', sortBy?, sortDesc?, filter_*)
 */
export function buildRequestParams(options, state) {
  const page = (options && options.page != null) ? options.page : state.pageNumber;
  let rowsPerPage = (options && options.itemsPerPage != null) ? options.itemsPerPage : state.numberOfRows;
  const sortByRaw = (options && options.sortBy != null) ? options.sortBy : state.serverSortBy;
  const sortDescRaw = (options && options.sortDesc != null) ? options.sortDesc : state.serverSortDesc;
  const sortByArr = [].concat(toArray(sortByRaw).filter(Boolean));
  const sortDescArr = [].concat(normalizeSortDesc(sortDescRaw));
  while (sortDescArr.length < sortByArr.length) sortDescArr.push(false);

  const params = { page, rowsPerPage, total: 'true' };
  if (sortByArr.length > 0) {
    const desc = sortDescArr.slice(0, sortByArr.length).map(Boolean);
    params.sortBy = sortByArr.join(',');
    params.sortDesc = desc.map((d) => d ? 'true' : 'false').join(',');
  }
  const filters = state.searchFilters || {};
  Object.keys(filters).forEach((key) => {
    const v = filters[key];
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      if (v.from != null && String(v.from).trim() !== '') {
        params['filter_' + key + '_from'] = String(v.from).trim();
      }
      if (v.to != null && String(v.to).trim() !== '') {
        params['filter_' + key + '_to'] = String(v.to).trim();
      }
    } else if (v != null && String(v).trim() !== '') {
      params['filter_' + key] = v;
    }
  });
  return { params, page, rowsPerPage, sortByArr, sortDescArr };
}

/**
 * Monta o objeto de parâmetros para buscar todos os registros (exportação CSV/PDF).
 * Preferir {@link fetchAllServerPagesForExport} para volumes grandes (evita timeout e o teto de página).
 * @param {Object} state - { total, serverSortBy, serverSortDesc, searchFilters }
 * @returns {Object} params para axios
 */
export function buildExportParams(state) {
  const sortByArr = [].concat(toArray(state.serverSortBy).filter(Boolean));
  const sortDescArr = [].concat(normalizeSortDesc(state.serverSortDesc));
  while (sortDescArr.length < sortByArr.length) sortDescArr.push(false);

  const params = { page: 1, rowsPerPage: state.total || 0, total: 'true' };
  if (sortByArr.length > 0) {
    const desc = sortDescArr.slice(0, sortByArr.length).map(Boolean);
    params.sortBy = sortByArr.join(',');
    params.sortDesc = desc.map((d) => d ? 'true' : 'false').join(',');
  }
  const filters = state.searchFilters || {};
  Object.keys(filters).forEach((key) => {
    const v = filters[key];
    if (v != null && typeof v === 'object' && !Array.isArray(v)) {
      if (v.from != null && String(v.from).trim() !== '') {
        params['filter_' + key + '_from'] = String(v.from).trim();
      }
      if (v.to != null && String(v.to).trim() !== '') {
        params['filter_' + key + '_to'] = String(v.to).trim();
      }
    } else if (v != null && String(v).trim() !== '') {
      params['filter_' + key] = v;
    }
  });
  return params;
}

/** Tamanho de página nas requisições de exportação (reduz timeout e memória por resposta). */
export const EXPORT_FETCH_PAGE_SIZE = 500;

/**
 * Busca todas as páginas do servidor para exportação CSV/PDF.
 * Respeita ordenação e filtros ativos; não fica limitado ao items-per-page da tela.
 *
 * @param {Object} options
 * @param {Function} options.fetchPage - async (params) => data ({ items, total } | Array)
 * @param {Object} options.state - { serverSortBy, serverSortDesc, searchFilters, total? }
 * @param {number} [options.pageSize=EXPORT_FETCH_PAGE_SIZE]
 * @param {Function} [options.onProgress] - (pct 0–100) => void
 * @param {Function} [options.mapItems] - (items) => items mapeados
 * @returns {Promise<Array>}
 */
export async function fetchAllServerPagesForExport({
  fetchPage,
  state,
  pageSize = EXPORT_FETCH_PAGE_SIZE,
  onProgress,
  mapItems,
}) {
  const chunk = Number(pageSize) > 0 ? Math.floor(Number(pageSize)) : EXPORT_FETCH_PAGE_SIZE;
  const all = [];
  let page = 1;
  let total = state && state.total != null ? Number(state.total) : NaN;
  if (Number.isNaN(total)) total = null;
  const report = typeof onProgress === 'function' ? onProgress : null;
  const map = typeof mapItems === 'function' ? mapItems : (items) => items;
  const yieldToUi = () => new Promise((r) => setTimeout(r, 0));

  if (report) report(0);

  while (page < 10000) {
    const pageState = {
      pageNumber: page,
      numberOfRows: chunk,
      serverSortBy: (state && state.serverSortBy) || [],
      serverSortDesc: (state && state.serverSortDesc) || [],
      searchFilters: (state && state.searchFilters) || {},
      lastItemsPerPageBeforeTodos: null,
      total: total != null ? total : 0,
    };
    const { params } = buildRequestParams(
      {
        page,
        itemsPerPage: chunk,
        sortBy: pageState.serverSortBy,
        sortDesc: pageState.serverSortDesc,
      },
      pageState
    );

    let data;
    try {
      data = await fetchPage(params);
    } catch (_err) {
      return map(all);
    }

    let items = [];
    if (data && typeof data.total === 'number' && Array.isArray(data.items)) {
      total = data.total;
      items = data.items;
    } else if (Array.isArray(data)) {
      items = data;
      total = items.length;
    } else {
      break;
    }

    all.push(...items);
    if (report && total != null && total > 0) {
      report(Math.min(100, Math.round((all.length / total) * 100)));
    }
    await yieldToUi();

    if (items.length === 0) break;
    if (total != null && all.length >= total) break;
    if (!(data && typeof data.total === 'number') && Array.isArray(data)) break;
    page += 1;
  }

  if (report) report(100);
  return map(all);
}

/**
 * Calcula o estado da paginação após a resposta da API (total, numberOfRows, etc.).
 * @param {Object} data - resposta { total, items }
 * @param {Object} options - { page, rowsPerPage } (do buildRequestParams)
 * @param {Object} currentState - state atual (numberOfRows, lastItemsPerPageBeforeTodos)
 * @param {number} [itemsPerPageLastOption=1000] - mesma opção final do footer (MerusDataTable) para modo "todos" (-1)
 * @returns {Object} atualizações para o state
 */
export function computePageState(data, options, currentState, itemsPerPageLastOption = 1000) {
  const { page, rowsPerPage } = options;
  const total = data && typeof data.total === 'number' ? data.total : 0;
  let numberOfRows = currentState.numberOfRows;
  let lastItemsPerPageBeforeTodos = currentState.lastItemsPerPageBeforeTodos;
  const lastOpt = Number(itemsPerPageLastOption) > 0 ? Number(itemsPerPageLastOption) : 1000;

  if (total < 1) {
    numberOfRows = rowsPerPage;
  } else if (total < lastOpt && rowsPerPage === lastOpt) {
    lastItemsPerPageBeforeTodos = lastOpt;
    numberOfRows = -1;
  } else if (total > lastOpt && currentState.numberOfRows === -1 && lastItemsPerPageBeforeTodos != null) {
    numberOfRows = lastItemsPerPageBeforeTodos;
  } else {
    numberOfRows = rowsPerPage;
  }

  return {
    total,
    pageNumber: page,
    numberOfRows,
    lastItemsPerPageBeforeTodos,
    serverSortBy: options.sortByArr && options.sortByArr.length > 0 ? options.sortByArr : [],
    serverSortDesc: options.sortDescArr && options.sortDescArr.length > 0 ? options.sortDescArr : [],
  };
}


/**
 * Inicializa serverSortBy e serverSortDesc a partir do defaultSort do componente.
 * @param {Array} defaultSort - ex: [{ value: 'nome', options: { value: 1 } }]
 * @returns {Object} { serverSortBy, serverSortDesc }
 */
export function initSortFromDefault(defaultSort) {
  if (!defaultSort || defaultSort.length === 0) {
    return { serverSortBy: [], serverSortDesc: [] };
  }
  return {
    serverSortBy: defaultSort.map((s) => s.value),
    serverSortDesc: defaultSort.map((s) => s.options && s.options.value === -1),
  };
}

/**
 * Verifica se a opção atual é a mesma página/ordenacao (evita requisição duplicada).
 */
export function samePageAndSort(opts, state) {
  if (!opts) return false;
  const page = opts.page != null ? opts.page : state.pageNumber;
  let rowsPerPage = opts.itemsPerPage != null ? opts.itemsPerPage : state.numberOfRows;
  const byRaw = opts.sortBy != null ? opts.sortBy : state.serverSortBy;
  const descRaw = opts.sortDesc != null ? opts.sortDesc : state.serverSortDesc;
  const byArr = [].concat(toArray(byRaw));
  const descArr = [].concat(normalizeSortDesc(descRaw));
  const samePage = page === state.pageNumber && rowsPerPage === state.numberOfRows;
  const sameSort =
    byArr.length === state.serverSortBy.length &&
    byArr.every((c, i) => c === state.serverSortBy[i]) &&
    descArr.length === state.serverSortDesc.length &&
    descArr.every((d, i) => !!d === !!state.serverSortDesc[i]);
  return samePage && sameSort;
}
